/**
 * Navbar menu structure: Tasks, Procurement, Work Requests and Admin (V2-035, NAV-09/12/13).
 *
 * Admin is a single top-level menu of groups; each group is a flyout of pages.
 * A group (and Admin itself) is shown when the user can open at least one page
 * inside it — the same permission keys the routes already use.
 *
 * Pages that are not built yet (shifts, thresholds, sites, inspection setup, …)
 * are omitted so the menu never links to a 404.
 */

export type HasPermission = (key: string) => boolean;

export interface AdminNavItem {
  label: string;
  to: string;
  visible: (has: HasPermission) => boolean;
  /** Shows the awaiting-approval count for this queue (loaded per `to` in awaitingApprovalsCount.ts). */
  countsAwaiting?: boolean;
}

export interface AdminNavGroup {
  id: string;
  label: string;
  items: AdminNavItem[];
}

const anyOf =
  (...keys: string[]) =>
  (has: HasPermission): boolean =>
    keys.some((key) => has(key));

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: 'people-access',
    label: 'People & Access',
    items: [
      {
        label: 'Employees',
        to: '/users?tab=employees',
        visible: anyOf('user:create', 'employee:view'),
      },
      {
        label: 'Classifications & Rates',
        to: '/users?tab=classifications',
        // Matches the Classifications tab on UsersPage (explicit key or user-admin).
        visible: anyOf('classification:view', 'user:create', 'user:edit'),
      },
      {
        label: 'Roles & Permissions',
        to: '/users?tab=roles',
        visible: anyOf('role:view'),
      },
    ],
  },
  {
    id: 'work-activities',
    label: 'Work & Activities',
    items: [
      {
        label: 'Activities',
        to: '/activities',
        visible: anyOf('activity:create'),
      },
      {
        label: 'Standing Tasks',
        to: '/standing-tasks',
        visible: anyOf('standing:tasks_view'),
      },
    ],
  },
  {
    id: 'areas-assets',
    label: 'Areas & Assets',
    items: [
      {
        label: 'Areas',
        to: '/areas',
        visible: anyOf('area:service_view', 'area:foreman_view', 'area:contract_view'),
      },
      {
        label: 'Assets',
        to: '/assets',
        visible: anyOf('asset:view'),
      },
    ],
  },
  {
    id: 'fleet-equipment',
    label: 'Fleet & Equipment',
    items: [
      {
        label: 'Vehicles',
        to: '/vehicles',
        visible: anyOf('vehicle:view'),
      },
      {
        label: 'Vehicle Profiles',
        to: '/vehicle-profiles',
        visible: anyOf('vehicle:profile:manage', 'vehicle:edit'),
      },
      {
        label: 'Fuel Surcharge',
        to: '/admin/fuel-surcharge',
        visible: anyOf('fuel_management:view'),
      },
    ],
  },
  {
    id: 'reference-data',
    label: 'Reference Data',
    items: [
      {
        label: 'Animal Types',
        to: '/admin/data-sources/animal-types',
        visible: anyOf('data_sources:view'),
      },
      {
        label: 'CHRIS Properties',
        to: '/admin/data-sources/chris-properties',
        visible: anyOf('data_sources:view'),
      },
      {
        label: 'Rockfall',
        to: '/admin/data-sources/rockfall',
        visible: anyOf('data_sources:view'),
      },
      {
        label: 'WARS',
        to: '/admin/data-sources/wars',
        visible: anyOf('data_sources:view'),
      },
    ],
  },
];

/**
 * The four Tasks views (V2-036). Tasks is one direct link in the bar; these gate its views.
 * All Tasks has no gate of its own: it shows whenever Tasks does.
 */
export const TASK_MANAGEMENT_NAV_ITEMS: AdminNavItem[] = [
  {
    label: 'Quantified',
    to: '/tasks/quantified',
    visible: anyOf('quantified:task_create', 'quantified:view', 'quantified:sign_work_only'),
  },
  {
    label: 'Routine',
    to: '/tasks/routine',
    visible: anyOf('routine:specific_create', 'routine:view'),
  },
  {
    label: 'Project',
    to: '/tasks/project',
    visible: anyOf('project:task_edit', 'project:task_create', 'project:view'),
  },
  {
    label: 'All',
    to: '/tasks/all',
    visible: () => true,
  },
];

/** Tasks shows when the user can open at least one of the gated views. */
export function isTasksVisible(has: HasPermission): boolean {
  return TASK_MANAGEMENT_NAV_ITEMS.filter((item) => item.to !== '/tasks/all').some((item) => item.visible(has));
}

/**
 * Procurement menu (V2-035). Vendors and Resources moved here from Admin; Vendor Approvals moved
 * here from the old Approvals menu, which is gone. Its awaiting count shows on the item and on
 * Procurement, and only to people who can act on vendor approvals (the item's own gate).
 * Material Locations (V2-039) is the Materials locations page.
 */
export const PROCUREMENT_NAV_ITEMS: AdminNavItem[] = [
  {
    label: 'Purchase Orders',
    to: '/purchase-orders',
    visible: anyOf('po:view'),
  },
  {
    label: 'Vendors',
    to: '/vendors',
    // heMaster:vendor_read is picker-only (HE form) and must not open this page.
    visible: anyOf('heMaster:vendor_view'),
  },
  {
    // The all-vendor resource catalogue (L1/M1). Same gate as Vendors.
    label: 'Resources',
    to: '/resources',
    visible: anyOf('heMaster:vendor_view'),
  },
  {
    label: 'Vendor Approvals',
    to: '/vendor-approvals',
    visible: anyOf('heMaster:vendor_approve'),
    countsAwaiting: true,
  },
  {
    // The Materials locations page. Auth-gated only (no catalogue permission), as before.
    label: 'Material Locations',
    to: '/locations',
    visible: () => true,
  },
];

/** Work Requests menu (V2-035): requests that come in from outside the crew. */
export const WORK_REQUESTS_NAV_ITEMS: AdminNavItem[] = [
  {
    label: 'PCRs',
    to: '/pcrs',
    visible: anyOf('submission:view', 'pcr:create'),
  },
  {
    label: 'Work IDs',
    to: '/work-ids',
    visible: anyOf('workId:view'),
  },
];

export function visibleItems(items: AdminNavItem[], has: HasPermission): AdminNavItem[] {
  return items.filter((item) => item.visible(has));
}

export function visibleGroupItems(group: AdminNavGroup, has: HasPermission): AdminNavItem[] {
  return visibleItems(group.items, has);
}

export function isGroupVisible(group: AdminNavGroup, has: HasPermission): boolean {
  return visibleGroupItems(group, has).length > 0;
}

export function isAdminMenuVisible(has: HasPermission): boolean {
  return ADMIN_NAV_GROUPS.some((group) => isGroupVisible(group, has));
}

/**
 * Top-level Equipment link (prototype Fleet list). Same permission as the
 * Vehicles admin page — a direct bar item, not nested under Admin.
 */
export function isEquipmentVisible(has: HasPermission): boolean {
  return has('vehicle:view');
}
